import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight, PartyPopper } from 'lucide-react';
import {
  readWorkbookJson,
  writeActivityScore,
  writeWorkbookJson,
  type WorkbookActivityCompletion,
} from './workbookPersist';

export type FillBlankItem = { q_no: number; template: string; correct?: string };

export interface FillBlankCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  wordBank: string[];
  items: FillBlankItem[];
  correctAnswers?: string[];
  sourcePage?: number;
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

function blanksInTemplate(template: string): number {
  return template.split('____').length - 1;
}

type Verdict = 'none' | 'ok' | 'bad';

type PersistShape = {
  values: Record<number, string[]>;
  itemIndex: number;
  verdict?: Record<string, Verdict>;
};

export const FillBlankCard: React.FC<FillBlankCardProps> = ({
  workbookLabel,
  title,
  instructions,
  wordBank,
  items,
  correctAnswers,
  sourcePage,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-fb-${persistenceId}` : '';

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const itemsStableKey = items.map((it) => `${it.q_no}\t${it.template}`).join('\n');

  const initialValues = useMemo(() => {
    const init: Record<number, string[]> = {};
    for (const it of items) {
      const n = Math.max(1, blanksInTemplate(it.template));
      init[it.q_no] = Array.from({ length: n }, () => '');
    }
    return init;
  }, [items]);

  const [values, setValues] = useState<Record<number, string[]>>(initialValues);
  const [itemIndex, setItemIndex] = useState(0);
  const [focused, setFocused] = useState<{ q: number; i: number } | null>(null);
  const [verdict, setVerdict] = useState<Record<number, Verdict>>({});

  const emptyVerdictFor = useCallback((list: FillBlankItem[]) => {
    const v: Record<number, Verdict> = {};
    for (const row of list) v[row.q_no] = 'none';
    return v;
  }, []);

  useEffect(() => {
    const list = itemsRef.current;
    const empty: Record<number, string[]> = {};
    for (const it of list) {
      const n = Math.max(1, blanksInTemplate(it.template));
      empty[it.q_no] = Array.from({ length: n }, () => '');
    }

    if (!storageKey) {
      setValues(empty);
      setItemIndex(0);
      setFocused(null);
      setVerdict(emptyVerdictFor(list));
      return;
    }
    const loaded = readWorkbookJson<Partial<PersistShape> | null>(storageKey, null);
    const nextVerdict = emptyVerdictFor(list);
    if (loaded?.verdict && typeof loaded.verdict === 'object') {
      const rawV = loaded.verdict as Record<string, unknown>;
      for (const k of Object.keys(rawV)) {
        const q = Number(k);
        if (!Number.isFinite(q) || nextVerdict[q] === undefined) continue;
        const t = rawV[k];
        if (t === 'ok' || t === 'bad') nextVerdict[q] = t;
      }
    }
    if (loaded?.values && typeof loaded.values === 'object') {
      const merged = { ...empty };
      const rawVals = loaded.values as Record<string, unknown>;
      for (const k of Object.keys(rawVals)) {
        const q = Number(k);
        if (!Number.isFinite(q)) continue;
        const arr = rawVals[k];
        if (Array.isArray(arr) && merged[q]?.length === arr.length) merged[q] = arr.map(String);
      }
      setValues(merged);
    } else {
      setValues(empty);
    }
    const maxI = Math.max(0, list.length - 1);
    const ii = typeof loaded?.itemIndex === 'number' ? Math.min(Math.max(0, loaded.itemIndex), maxI) : 0;
    setItemIndex(ii);
    setFocused(null);
    setVerdict(nextVerdict);
  }, [storageKey, itemsStableKey, items.length, emptyVerdictFor]);

  useEffect(() => {
    if (!storageKey) return;
    const verdictOut: Record<string, Verdict> = {};
    for (const row of items) {
      verdictOut[String(row.q_no)] = verdict[row.q_no] ?? 'none';
    }
    writeWorkbookJson(storageKey, { values, itemIndex, verdict: verdictOut } satisfies PersistShape);
  }, [storageKey, values, itemIndex, verdict, items]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const hasKey = useMemo(() => {
    if (correctAnswers && correctAnswers.length === items.length) return true;
    return items.some((it) => typeof it.correct === 'string' && it.correct.trim() !== '');
  }, [correctAnswers, items]);

  const norm = (s: string) => s.trim().replace(/\s+/g, ' ');

  const safeIdx = Math.min(Math.max(0, itemIndex), Math.max(0, items.length - 1));
  const it = items[safeIdx];
  const nBlanks = it ? Math.max(1, blanksInTemplate(it.template)) : 1;
  const blanks = it ? values[it.q_no] ?? Array.from({ length: nBlanks }, () => '') : [];

  const setBlank = (qNo: number, blankIndex: number, v: string) => {
    if ((verdict[qNo] ?? 'none') !== 'none') return;
    mark();
    setValues((prev) => {
      const cur = [...(prev[qNo] ?? [])];
      cur[blankIndex] = v;
      return { ...prev, [qNo]: cur };
    });
  };

  const onChipTap = (word: string) => {
    if (!it || (verdict[it.q_no] ?? 'none') !== 'none') return;
    mark();
    let i = 0;
    if (focused && focused.q === it.q_no) i = focused.i;
    else {
      const arr = values[it.q_no] ?? [];
      const emptyIdx = arr.findIndex((x) => !x.trim());
      i = emptyIdx >= 0 ? emptyIdx : 0;
    }
    setBlank(it.q_no, i, word);
    setFocused({ q: it.q_no, i: (i + 1) % nBlanks });
  };

  const itemOk = useCallback(
    (row: FillBlankItem, idx: number): boolean => {
      const n = Math.max(1, blanksInTemplate(row.template));
      const entered = values[row.q_no] ?? Array.from({ length: n }, () => '');
      const expectedRaw =
        row.correct != null && row.correct !== '' ? row.correct : correctAnswers?.[idx];
      const expectedParts = expectedRaw != null ? expectedRaw.split('|').map((x) => norm(x)) : [];
      return (
        expectedParts.length === n && entered.every((v, i) => norm(v) === (expectedParts[i] ?? ''))
      );
    },
    [values, correctAnswers]
  );

  const currentVerdict = it ? verdict[it.q_no] ?? 'none' : 'none';
  const currentItemOk = it && hasKey ? itemOk(it, safeIdx) : false;

  const allJudged =
    hasKey &&
    items.length > 0 &&
    items.every((row) => {
      const v = verdict[row.q_no] ?? 'none';
      return v === 'ok' || v === 'bad';
    });
  const allJudgedOk = allJudged && items.every((row, idx) => itemOk(row, idx));

  useEffect(() => {
    if (!persistenceId || !hasKey || !allJudged || items.length === 0) return;
    const okCount = items.filter((row, idx) => itemOk(row, idx)).length;
    writeActivityScore(persistenceId, (okCount / items.length) * 100);
  }, [persistenceId, hasKey, allJudged, items, itemOk]);

  useEffect(() => {
    if (!onWorkbookStatusChange || items.length === 0) return;
    const allFilled = items.every((row) => {
      const n = Math.max(1, blanksInTemplate(row.template));
      const arr = values[row.q_no] ?? Array.from({ length: n }, () => '');
      return arr.every((x) => x.trim().length > 0);
    });
    if (!allFilled) {
      const any = items.some((row) => {
        const n = Math.max(1, blanksInTemplate(row.template));
        const arr = values[row.q_no] ?? [];
        return arr.some((x) => x.trim().length > 0);
      });
      onWorkbookStatusChange(any ? 'in_progress' : 'not_started');
      return;
    }
    if (!hasKey) {
      onWorkbookStatusChange('completed');
      return;
    }
    if (allJudgedOk) {
      onWorkbookStatusChange('completed');
      return;
    }
    onWorkbookStatusChange('in_progress');
  }, [onWorkbookStatusChange, items, values, hasKey, allJudgedOk]);

  if (!it) {
    return (
      <div className="rounded-[2rem] border-4 border-sky-200 bg-sky-50 p-8 text-center tamil-text" lang="ta">
        வாக்கியங்கள் இல்லை
      </div>
    );
  }

  const locked = currentVerdict !== 'none';
  const parts = it.template.split('____');

  return (
    <div className="overflow-hidden rounded-[2rem] border-4 border-sky-300/90 bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50 shadow-[0_20px_50px_-12px_rgba(14,165,233,0.35)] dark:border-sky-700/60 dark:from-sky-950/40 dark:via-slate-900 dark:to-slate-900">
      <div className="border-b border-sky-200/70 bg-white/55 px-5 py-4 backdrop-blur-sm dark:border-sky-800/40 dark:bg-slate-900/50">
        {workbookLabel ? (
          <p className="text-sm font-bold text-sky-800 tamil-text dark:text-sky-200" lang="ta">
            {workbookLabel}
          </p>
        ) : null}
        <h2 className="mt-1 text-2xl font-extrabold text-sky-950 tamil-text md:text-3xl dark:text-sky-50" lang="ta">
          {title}
        </h2>
        {instructions ? (
          <p className="mt-2 text-lg leading-relaxed text-sky-900/95 tamil-text dark:text-sky-100/90" lang="ta">
            {instructions}
          </p>
        ) : null}
        {sourcePage != null ? (
          <span className="mt-3 inline-block rounded-full bg-sky-600 px-3 py-1 text-sm font-bold text-white">p.{sourcePage}</span>
        ) : null}
      </div>

      <div className="px-4 py-6 sm:px-8">
        {items.length > 1 ? (
          <div className="mb-6 flex items-center justify-between gap-3">
            <button
              type="button"
              disabled={safeIdx <= 0}
              onClick={() => {
                mark();
                setItemIndex((i) => i - 1);
                setFocused(null);
              }}
              className="inline-flex min-h-[48px] items-center gap-1 rounded-2xl bg-white px-4 py-2 text-base font-bold text-sky-900 shadow-md disabled:opacity-40 dark:bg-slate-800 dark:text-sky-100 tamil-text"
              lang="ta"
            >
              <ChevronLeft className="h-5 w-5" />
              முந்தைய
            </button>
            <span className="text-lg font-extrabold text-sky-900 tamil-text dark:text-sky-100" lang="ta">
              {safeIdx + 1} / {items.length}
            </span>
            <button
              type="button"
              disabled={safeIdx >= items.length - 1}
              onClick={() => {
                mark();
                setItemIndex((i) => i + 1);
                setFocused(null);
              }}
              className="inline-flex min-h-[48px] items-center gap-1 rounded-2xl bg-white px-4 py-2 text-base font-bold text-sky-900 shadow-md disabled:opacity-40 dark:bg-slate-800 dark:text-sky-100 tamil-text"
              lang="ta"
            >
              அடுத்து
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        ) : null}

        {wordBank.length > 0 ? (
          <div className="mb-6">
            <p className="mb-3 text-center text-base font-extrabold text-sky-800 tamil-text dark:text-sky-200" lang="ta">
              சொல் கொட்டில் — தட்டிப் பூர்த்தி செய்யவும்
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {wordBank.map((w) => (
                <button
                  key={w}
                  type="button"
                  disabled={locked}
                  onClick={() => onChipTap(w)}
                  className="min-h-[52px] min-w-[52px] rounded-2xl border-4 border-white bg-gradient-to-br from-fuchsia-400 to-pink-400 px-4 py-3 text-lg font-extrabold text-white shadow-lg transition hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 tamil-text"
                  lang="ta"
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="rounded-3xl border-4 border-white bg-white/95 p-6 shadow-lg dark:border-slate-600 dark:bg-slate-800/95">
          <p className="text-center text-xl font-bold leading-relaxed text-slate-900 tamil-text md:text-2xl dark:text-white" lang="ta">
            {parts.map((part, i) => (
              <React.Fragment key={i}>
                <span>{part}</span>
                {i < parts.length - 1 ? (
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => {
                      if (locked) return;
                      mark();
                      setFocused({ q: it.q_no, i });
                    }}
                    className={clsx(
                      'mx-1 inline-flex min-h-[3rem] min-w-[6.5rem] items-center justify-center rounded-xl border-b-8 border-dashed px-2 text-lg font-bold transition tamil-text',
                      focused?.q === it.q_no && focused?.i === i
                        ? 'border-amber-500 bg-amber-100 text-amber-950 ring-4 ring-amber-300 dark:bg-amber-900/50 dark:text-amber-50'
                        : (blanks[i] ?? '').trim()
                          ? 'border-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-50'
                          : 'border-sky-400 bg-sky-50 text-sky-900 dark:border-sky-600 dark:bg-sky-950/40 dark:text-sky-50'
                    )}
                    lang="ta"
                  >
                    {(blanks[i] ?? '').trim() || '___'}
                  </button>
                ) : null}
              </React.Fragment>
            ))}
          </p>
          <p className="mt-4 text-center text-sm font-bold text-sky-700 tamil-text dark:text-sky-300" lang="ta">
            விசைப்பலகை / தட்டச்சு
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            {blanks.map((b, i) => (
              <input
                key={i}
                type="text"
                readOnly={locked}
                value={b}
                onChange={(e) => {
                  mark();
                  setBlank(it.q_no, i, e.target.value);
                }}
                onFocus={() => setFocused({ q: it.q_no, i })}
                aria-label={`இடைவெளி ${i + 1}`}
                className={clsx(
                  'min-h-[52px] w-40 rounded-2xl border-4 border-sky-300 bg-sky-50/80 px-3 text-center text-xl font-bold text-sky-950 outline-none focus:border-amber-400 dark:border-sky-700 dark:bg-slate-900 dark:text-white tamil-text',
                  locked && 'cursor-not-allowed opacity-70'
                )}
                lang="ta"
              />
            ))}
          </div>
        </div>

        {hasKey ? (
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              disabled={
                locked ||
                !blanks.every((x) => x.trim().length > 0)
              }
              onClick={() => {
                if (locked) return;
                mark();
                const ok = itemOk(it, safeIdx);
                setVerdict((prev) => ({ ...prev, [it.q_no]: ok ? 'ok' : 'bad' }));
              }}
              className="min-h-[52px] rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-8 py-3 text-lg font-extrabold text-white shadow-xl transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40 tamil-text"
              lang="ta"
            >
              பதில் சரிபார்
            </button>
          </div>
        ) : null}

        {hasKey && locked ? (
          <div
            className={clsx(
              'mt-6 flex flex-col items-center justify-center gap-2 rounded-3xl border-4 p-6 text-center tamil-text',
              currentItemOk
                ? 'border-emerald-400 bg-emerald-100 text-emerald-900 dark:border-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-50'
                : 'border-rose-400 bg-rose-50 text-rose-950 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-50'
            )}
            lang="ta"
          >
            {currentItemOk ? (
              <>
                <PartyPopper className="h-12 w-12 text-emerald-600" aria-hidden />
                <p className="text-2xl font-extrabold">சூப்பர்! இந்த வினா சரி.</p>
              </>
            ) : (
              <p className="text-xl font-bold">
                இந்த வினாவில் பதிலை மாற்ற இயலாது. மீண்டும் முயல் மூலம் முழுச் செயலை மீட்டமைத்து மீண்டும் முயலவும்.
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
